import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Paper,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
} from '@mui/material';
import {
  AccessTime,
  Business,
  Person,
  Event,
  ContentCopy,
  Delete,
} from '@mui/icons-material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { format, startOfDay, isBefore } from 'date-fns';
import { toZonedTime, formatInTimeZone } from 'date-fns-tz';
import { apiService } from '../services/apiService';
import { studioIdOf } from '../utils/studioOf';
import { colors } from '../theme/theme';
import { useAuth } from '../contexts/AuthContext';
import CopyDayDialog from '../components/CopyDayDialog';
import { useMyLocations } from '../hooks/useMyLocations';

interface Session {
  _id: string;
  className: string;
  date: string;
  slots: number;
  availableSlots: number;
  price: number;
  duration: number;
  image?: string;
  class?: {
    _id: string;
  };
  studio: {
    _id: string;
    studioName: string;
    address?: string;
  };
  instructor: {
    _id: string;
    name: string;
    image?: string;
  };
  classType: {
    _id: string;
    name: string;
  };
}

const ActiveClassesPage: React.FC = () => {
  // This page requires only `classes.view`, which instructors hold — so its
  // Copy Day / Delete All / per-session Delete controls need the same session
  // permission the rest of the panel now asks for.
  const { canWriteSessions } = useAuth();
  // Under "All locations" an undated, brand-wide session read is refused /
  // unbounded — the calendar dots then ask for today (IST) only.
  const { isAllLocations } = useMyLocations();
  const navigate = useNavigate();
  const IST_TIMEZONE = 'Asia/Kolkata';

  // Get current date in IST
  const getCurrentISTDate = () => {
    const now = new Date();
    const istDate = toZonedTime(now, IST_TIMEZONE);
    return startOfDay(istDate);
  };

  // Initialize with IST date
  const [selectedDate, setSelectedDate] = useState<Date>(getCurrentISTDate());
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [datesWithSessions, setDatesWithSessions] = useState<Set<string>>(new Set());
  const [copyDayDialogOpen, setCopyDayDialogOpen] = useState(false);
  const [deleteDateDialogOpen, setDeleteDateDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteSessionDialogOpen, setDeleteSessionDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [deletingSession, setDeletingSession] = useState(false);

  const fetchSessions = useCallback(async (date: Date) => {
    try {
      setIsLoading(true);
      setError(null);
      const istFormatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      const dateStr = istFormatter.format(date);
      const response = await apiService.getActiveSessionsByDate({ date: dateStr });
      setSessions(response.sessions || []);
    } catch (err: any) {
      console.error('Error fetching active sessions:', err);
      setError(err.message || 'Failed to fetch active sessions');
      setSessions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchAllSessions = useCallback(async () => {
    try {
      const todayIST = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date());
      const response = await apiService.getActiveSessionsByDate(
        isAllLocations ? { date: todayIST } : {},
      );
      const datesSet = new Set<string>();

      (response.sessions || []).forEach((session: Session) => {
        const sessionDate = new Date(session.date);
        const year = sessionDate.getUTCFullYear();
        const month = String(sessionDate.getUTCMonth() + 1).padStart(2, '0');
        const day = String(sessionDate.getUTCDate()).padStart(2, '0');
        const dateKey = `${year}-${month}-${day}`;
        datesSet.add(dateKey);
      });

      setDatesWithSessions(datesSet);
    } catch (err: any) {
      console.error('Error fetching all sessions:', err);
      setDatesWithSessions(new Set());
    }
  }, [isAllLocations]);

  useEffect(() => {
    fetchSessions(selectedDate);
  }, [selectedDate, fetchSessions]);

  useEffect(() => {
    fetchAllSessions();
  }, [fetchAllSessions]);

  const handleDateSelect = (date: Date | null) => {
    if (!date) return;
    // Only allow selecting today or future dates
    setSelectedDate(date);
  };

  const shouldDisableDate = (date: Date) => {
    // Disable past dates - only allow today and future dates
    const today = getCurrentISTDate();
    const dateToCheck = startOfDay(date);
    return isBefore(dateToCheck, today);
  };

  const hasSessions = (date: Date) => {
    const istFormatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const istDateKey = istFormatter.format(date);
    return datesWithSessions.has(istDateKey);
  };

  const handleCopyDaySuccess = () => {
    // Refresh sessions and dates with sessions
    fetchSessions(selectedDate);
    fetchAllSessions();
  };

  const handleDeleteDateClick = () => {
    setDeleteDateDialogOpen(true);
  };

  const handleDeleteDateConfirm = async () => {
    try {
      setDeleting(true);
      const istFormatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      const dateStr = istFormatter.format(selectedDate);
      
      const response = await apiService.deleteSessionsByDate(dateStr);
      
      // Show success message
      setError(null);
      
      // Refresh sessions and dates
      await fetchSessions(selectedDate);
      await fetchAllSessions();
      
      setDeleteDateDialogOpen(false);
      
      // Show success alert (you can replace this with a snackbar if you have one)
      alert(`Success! ${response.message || `Deleted ${response.deletedCount || 0} session(s).`}`);
    } catch (err: any) {
      setError(err.message || 'Failed to delete sessions');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSessionClick = (session: Session, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    setSelectedSession(session);
    setDeleteSessionDialogOpen(true);
  };

  const handleDeleteSessionConfirm = async () => {
    if (!selectedSession) return;

    try {
      setDeletingSession(true);
      // Pinned to the session's own location — under "All locations" the
      // switcher sends __all__, which the server refuses for writes.
      await apiService.deleteSession(selectedSession._id, studioIdOf(selectedSession.studio));

      // Refetch from the server rather than patching local state by hand —
      // same pattern as handleDeleteDateConfirm / handleCopyDaySuccess above,
      // and it keeps datesWithSessions (the calendar dots) correct without
      // reimplementing that logic here.
      await fetchSessions(selectedDate);
      await fetchAllSessions();

      setDeleteSessionDialogOpen(false);
      setSelectedSession(null);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete session');
    } finally {
      setDeletingSession(false);
    }
  };

  // Custom day component to show dots for dates with sessions
  const ServerDay = (props: PickersDayProps) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const hasSession = hasSessions(day);
    // Only show dot if the date has a session AND is within the current month
    const shouldShowDot = hasSession && !outsideCurrentMonth;

    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <PickersDay {...other} day={day} outsideCurrentMonth={outsideCurrentMonth} />
        {shouldShowDot && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 2,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 4,
              height: 4,
              borderRadius: '50%',
              backgroundColor: colors.primary,
            }}
          />
        )}
      </Box>
    );
  };

  const formatSessionTime = (dateString: string) => {
    const date = new Date(dateString);
    return formatInTimeZone(date, 'Asia/Kolkata', 'hh:mm a');
  };

  const formatSessionDate = (dateString: string) => {
    const date = new Date(dateString);
    return formatInTimeZone(date, 'Asia/Kolkata', 'EEE, MMM d');
  };

  const renderCalendar = () => {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Paper elevation={2} sx={{ p: 1.5, height: 'fit-content', maxWidth: 350 }}>
          <DateCalendar
            value={selectedDate}
            onChange={handleDateSelect}
            shouldDisableDate={shouldDisableDate}
            slots={{
              day: ServerDay,
            }}
            sx={{
              width: '100%',
              '& .MuiPickersCalendarHeader-root': {
                paddingLeft: 1,
                paddingRight: 1,
              },
              '& .MuiDayCalendar-weekDayLabel': {
                fontSize: '0.75rem',
                fontWeight: 500,
              },
              '& .MuiPickersDay-root': {
                fontSize: '0.875rem',
              },
            }}
          />
        </Paper>
      </LocalizationProvider>
    );
  };

  const renderSessionCard = (session: Session) => {
    const handleCardClick = () => {
      // Navigate to class detail if class reference exists
      if (session.class?._id) {
        navigate(`/classes/${session.class._id}`);
      }
    };

    return (
      <Card
        key={session._id}
        onClick={handleCardClick}
        sx={{
          mb: 2,
          cursor: session.class?._id ? 'pointer' : 'default',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 4,
            transform: session.class?._id ? 'translateY(-2px)' : 'none',
          },
        }}
      >
      <CardContent>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {session.image && (
            <Avatar
              src={session.image}
              variant="rounded"
              sx={{ width: 80, height: 80 }}
            >
              {session.className.charAt(0)}
            </Avatar>
          )}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
              <Typography variant="h6" fontWeight={700}>
                {session.className}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  label={`₹${session.price}`}
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
                {canWriteSessions && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => handleDeleteSessionClick(session, e)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'error.light',
                        color: 'white',
                      },
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Chip
                icon={<AccessTime />}
                label={`${session.duration} min`}
                size="small"
                variant="outlined"
              />
              <Chip
                icon={<Person />}
                label={`${session.availableSlots}/${session.slots} slots`}
                size="small"
                variant="outlined"
                color={session.availableSlots === 0 ? 'error' : 'default'}
              />
              <Chip
                label={session.classType?.name || 'N/A'}
                size="small"
                variant="outlined"
              />
              <Chip
                icon={<Business />}
                label={session.studio?.studioName || 'N/A'}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontWeight: 600 }}
              />
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Event fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {formatSessionDate(session.date)} at {formatSessionTime(session.date)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Studio: {session.studio?.studioName || 'N/A'}
                  {session.studio?.address && ` - ${session.studio.address}`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Instructor: {session.instructor?.name || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Active Classes Calendar
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage active classes by date (IST timezone)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {canWriteSessions && (
            <>
              <Button
                variant="contained"
                startIcon={<ContentCopy />}
                onClick={() => setCopyDayDialogOpen(true)}
                sx={{ height: 'fit-content' }}
              >
                Copy Day
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={handleDeleteDateClick}
                disabled={sessions.length === 0 || isLoading}
                sx={{ height: 'fit-content' }}
              >
                Delete All for Date
              </Button>
            </>
          )}
        </Box>
      </Box>
      <Box sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        {/* Calendar Section */}
        <Grid size={{ xs: 12, md: 4 }}>
          {renderCalendar()}
        </Grid>

        {/* Sessions List Section */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper elevation={2} sx={{ p: 2, height: '100%', maxHeight: '80vh', overflow: 'auto' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Active Classes for {format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </Typography>

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            ) : sessions.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No active classes found for the selected date.
              </Alert>
            ) : (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Found {sessions.length} active {sessions.length === 1 ? 'class' : 'classes'}
                </Typography>
                {sessions.map(renderSessionCard)}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Summary Stats */}
      <Box sx={{ mt: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Total Active Classes
                </Typography>
                <Typography variant="h4" fontWeight={700} color={colors.primary}>
                  {sessions.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Total Available Slots
                </Typography>
                <Typography variant="h4" fontWeight={700} color={colors.success}>
                  {sessions.reduce((sum, s) => sum + s.availableSlots, 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Total Booked Slots
                </Typography>
                <Typography variant="h4" fontWeight={700} color={colors.error}>
                  {sessions.reduce((sum, s) => sum + (s.slots - s.availableSlots), 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Copy Day Dialog */}
      <CopyDayDialog
        open={copyDayDialogOpen}
        onClose={() => setCopyDayDialogOpen(false)}
        targetDate={selectedDate}
        onSuccess={handleCopyDaySuccess}
      />

      {/* Delete Date Confirmation Dialog */}
      <Dialog
        open={deleteDateDialogOpen}
        onClose={() => !deleting && setDeleteDateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete All Sessions for Date</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all sessions for{' '}
            <strong>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</strong>?
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              ⚠️ Important Notes:
            </Typography>
            <Typography variant="body2" component="div">
              • Sessions with existing bookings will <strong>NOT</strong> be deleted
              <br />
              • Only sessions without bookings will be removed
              <br />
              • This action cannot be undone
            </Typography>
          </Alert>
          {sessions.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Found <strong>{sessions.length}</strong> session(s) for this date.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDateDialogOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteDateConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete Sessions'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Session Confirmation Dialog */}
      <Dialog
        open={deleteSessionDialogOpen}
        onClose={() => !deletingSession && setDeleteSessionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Session</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the session{' '}
            <strong>"{selectedSession?.className}"</strong>?
          </DialogContentText>
          {selectedSession && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Date & Time:</strong> {formatSessionDate(selectedSession.date)} at {formatSessionTime(selectedSession.date)}
                <br />
                <strong>Studio:</strong> {selectedSession.studio?.studioName || 'N/A'}
                <br />
                <strong>Instructor:</strong> {selectedSession.instructor?.name || 'N/A'}
              </Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              ⚠️ Important Notes:
            </Typography>
            <Typography variant="body2" component="div">
              • Sessions with existing bookings will <strong>NOT</strong> be deleted
              <br />
              • This action cannot be undone
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteSessionDialogOpen(false);
              setSelectedSession(null);
            }}
            disabled={deletingSession}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSessionConfirm}
            color="error"
            variant="contained"
            disabled={deletingSession}
            startIcon={deletingSession ? <CircularProgress size={16} /> : <Delete />}
          >
            {deletingSession ? 'Deleting...' : 'Delete Session'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActiveClassesPage;

